import type { ReportCatalogColumn, ReportDefinitionInput } from "@cx/contracts";
import {
  REPORT_MAX_GROUP,
  formatBRL,
  formatPct,
  formatQty,
  toPct4,
  toQ4,
  parseBRL,
  type ReportAgg,
  type ReportFilterValue,
  type ReportOp,
} from "@cx/domain";

/** Coluna do editor: chave e resumo ("" = sem resumo). */
export type EditorColumn = { key: string; agg: ReportAgg | "" };
/** Filtro do editor com os valores como o usuário digitou (convertidos só ao montar a definição). */
export type EditorFilter = { key: string; op: ReportOp; text: string; text2: string; multi: string[] };
export type EditorSort = { key: string; agg: ReportAgg | ""; dir: "asc" | "desc" };
export type EditorState = {
  source: ReportDefinitionInput["source"];
  columns: EditorColumn[];
  filters: EditorFilter[];
  sort: EditorSort[];
};

const NO_VALUE: ReadonlySet<ReportOp> = new Set(["empty", "not_empty"]);
export const opNeedsValue = (op: ReportOp) => !NO_VALUE.has(op);

/** Texto digitado → valor do filtro pelo tipo da coluna (moeda em centavos, quantidade em q4, % em pct4). */
export function parseFilterValue(col: ReportCatalogColumn, text: string): ReportFilterValue {
  const t = text.trim();
  if (!t) throw new Error(`Informe o valor do filtro "${col.label}".`);
  try {
    switch (col.type) {
      case "money":
        return parseBRL(t);
      case "qty":
        return toQ4(t);
      case "pct":
        return toPct4(t);
      case "int": {
        if (!/^-?\d{1,15}$/.test(t)) throw new Error();
        return Number(t);
      }
      case "bool":
        return t === "true";
      default:
        return t;
    }
  } catch {
    throw new Error(`Valor inválido no filtro "${col.label}".`);
  }
}

/**
 * Monta a definição da API. Com algum resumo, as colunas sem resumo viram o agrupamento (até 3), como no Bling;
 * erros de digitação viram mensagem em pt-BR (lançada como Error).
 */
export function buildDefinition(s: EditorState, cols: readonly ReportCatalogColumn[]): ReportDefinitionInput {
  const byKey = new Map(cols.map((c) => [c.key, c]));
  const col = (k: string) => {
    const c = byKey.get(k);
    if (!c) throw new Error("Escolha a coluna.");
    return c;
  };
  const aggregated = s.columns.some((c) => c.agg);
  const groupBy = aggregated ? s.columns.filter((c) => !c.agg).map((c) => c.key) : [];
  if (groupBy.length > REPORT_MAX_GROUP)
    throw new Error(`Com resumo, agrupe por até ${REPORT_MAX_GROUP} colunas (as colunas sem resumo).`);
  return {
    source: s.source,
    columns: s.columns.map((c) => ({ key: col(c.key).key, agg: c.agg || null })),
    filters: s.filters.map((f) => {
      const c = col(f.key);
      if (!opNeedsValue(f.op)) return { key: f.key, op: f.op };
      if (f.op === "between")
        return { key: f.key, op: f.op, values: [parseFilterValue(c, f.text), parseFilterValue(c, f.text2)] };
      if (f.op === "in") {
        const raw =
          c.type === "enum"
            ? f.multi
            : f.text
                .split(/[;,]/)
                .map((x) => x.trim())
                .filter(Boolean);
        if (!raw.length) throw new Error(`Escolha pelo menos um valor no filtro "${c.label}".`);
        return { key: f.key, op: f.op, values: raw.map((x) => parseFilterValue(c, x)) };
      }
      return { key: f.key, op: f.op, value: parseFilterValue(c, f.text) };
    }),
    groupBy,
    sort: s.sort.map((x) => ({ key: x.key, agg: x.agg || null, dir: x.dir })),
  };
}

/** Valor do filtro → texto do campo (moeda "2,50", quantidade "1,5", % "12,5"). */
export function filterText(
  type: ReportCatalogColumn["type"] | undefined,
  v: ReportFilterValue | null | undefined,
) {
  if (v === undefined || v === null) return "";
  if (typeof v === "number") {
    if (type === "money") return formatBRL(v).replace(/^(-?)R\$\s/, "$1");
    if (type === "qty") return formatQty(v, 0);
    if (type === "pct") return formatPct(v).replace("%", "");
  }
  return String(v);
}

/** Definição salva → estado do editor (valores voltam a texto pt-BR simples). */
export function editorFromDefinition(
  d: ReportDefinitionInput,
  cols: readonly ReportCatalogColumn[],
): EditorState {
  const types = new Map(cols.map((c) => [c.key, c.type]));
  return {
    source: d.source,
    columns: d.columns.map((c) => ({ key: c.key, agg: c.agg ?? "" })),
    filters: d.filters.map((f) => {
      const txt = (v: ReportFilterValue | null | undefined) => filterText(types.get(f.key), v);
      return {
        key: f.key,
        op: f.op,
        text:
          f.op === "between"
            ? txt(f.values?.[0])
            : f.op === "in"
              ? (f.values ?? []).map(txt).join(", ")
              : txt(f.value),
        text2: f.op === "between" ? txt(f.values?.[1]) : "",
        multi: f.op === "in" ? (f.values ?? []).map((v) => String(v)) : [],
      };
    }),
    sort: d.sort.map((x) => ({ key: x.key, agg: x.agg ?? "", dir: x.dir })),
  };
}
